if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.layout = this.dataset.layout;
        this.items = Number(this.dataset.items);
        this.playPauseBtn = null;
        this.isAutoplaying = false;

        this.selectedIndex = this.selectedIndex;

        if (this.classList.contains('slideshow-height--adapt')) {
          this.calcSlideHeight();
        }

        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: 'fade',
          on: {
            init: this.handleAfterInit.bind(this),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: true,
          };
        }

        switch (this.layout) {
          case 'centered':
            this.sliderOptions = {
              ...this.sliderOptions,
              centeredSlides: true,
              slidesPerView: 'auto',
              effect: 'slide',
            };
            break;
        }

        if (this.layout !== 'centered' && this.items === 1) {
          this.sliderOptions.loop = false;
          this.classList.remove('slideshow-controls-visible--yes');
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();
        this.handleSlideChange(this.sliderInstance.slider);

        // Pause autoplay on keyboard focus
        this.addEventListener('focusin', (event) => {
          if (event.target === this.playPauseBtn) return;

          if (this.sliderInstance?.slider?.autoplay?.running) {
            this.sliderInstance.slider.autoplay.stop();
            this.isAutoplaying = false;
            if (this.playPauseBtn) {
              this.playPauseBtn.setAttribute('aria-label', 'Play Slideshow');
            }
          }
        });
        // Resume autoplay if the play/pause button is focused or on focusout
        this.playPauseBtn.addEventListener('focusin', () => {
          const autoplayDelay = parseInt(this.dataset.autoplay);
          if (autoplayDelay > 0 && this.sliderInstance?.slider?.autoplay) {
            this.sliderInstance.slider.autoplay.start();
            this.isAutoplaying = true;
            this.playPauseBtn.setAttribute('aria-label', 'Pause Slideshow');
          }
        });
        this.addEventListener('focusout', () => {
          setTimeout(() => {
            if (!this.contains(document.activeElement)) {
              const autoplayDelay = parseInt(this.dataset.autoplay);
              if ( autoplayDelay > 0 && this.sliderInstance?.slider?.autoplay) {
                this.sliderInstance.slider.autoplay.start();
                this.isAutoplaying = true;
                if (this.playPauseBtn) {
                  this.playPauseBtn.setAttribute('aria-label', 'Pause Slideshow');
                }
              }
            }
          }, 50);
        });

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

        if (this.sliderInstance) {
          if (this.layout === 'centered' && !FoxTheme.config.mqlMobile) {
            const motionTextElements = Array.from(this.querySelectorAll('motion-element[data-text]'));
            motionTextElements &&
              motionTextElements.forEach((motionTextElement) => {
                if (
                  typeof motionTextElement.resetAnimation === 'function' &&
                  !motionTextElement.closest('.swiper-slide-active')
                ) {
                  motionTextElement.resetAnimation();
                }
              });
          }
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
          // Fix accessibility
          // const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          // if (this.layout === 'centered') {
          //   focusableElements.forEach((element) => {
          //     element.addEventListener('focusin', () => {
          //       const slide = element.closest('.swiper-slide');
          //       const slideToIndex = this.sliderInstance.slider.slides.indexOf(slide);
          //       this.sliderInstance.slider.slideToLoop(slideToIndex);
          //     });
          //   });
          // } else {
          //   focusableElements.forEach((element) => {
          //     element.addEventListener('focusin', () => {
          //       const slide = element.closest('.swiper-slide');
          //       this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          //     });
          //   });
          // }
        }
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            motionEl && motionEl.refreshAnimation();
          });
        }
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');

        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }

        if (this.sliderControls) {
          this.playPauseBtn = document.createElement('button');
          this.playPauseBtn.className = 'slideshow-toggle';
          this.playPauseBtn.setAttribute('aria-label', 'Pause Slideshow');
          this.playPauseBtn.innerHTML = 
          `<span class="slideshow-icon">
            <icon class="icon icon-pause" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-pause" viewBox="0 0 8 12"><path fill="currentColor" d="M1.2.75c-.387 0-.7.235-.7.525v9.45c0 .29.313.525.7.525s.7-.235.7-.525v-9.45c0-.29-.313-.525-.7-.525m5.6 0c-.387 0-.7.235-.7.525v9.45c0 .29.313.525.7.525s.7-.235.7-.525v-9.45c0-.29-.313-.525-.7-.525"/></svg></icon>
            <icon class="icon icon-play" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-play" viewBox="0 0 10 14"><path fill="currentColor" fill-rule="evenodd" d="M1.482.815A1 1 0 0 0 0 1.69v10.517a1 1 0 0 0 1.525.851L10.54 7.5a1 1 0 0 0-.043-1.728z" clip-rule="evenodd"/></svg></icon>
          </span>`;
          this.sliderControls.appendChild(this.playPauseBtn);
          this.isAutoplaying = !!this.sliderOptions.autoplay;

          this.playPauseBtn.addEventListener('click', () => {
            if (!this.sliderInstance?.slider) return;
        
            if (this.isAutoplaying) {
              this.sliderInstance.slider.autoplay.stop();
              this.playPauseBtn.setAttribute('aria-label', 'Play Slideshow');
            } else {
              this.sliderInstance.slider.autoplay.start();
              this.playPauseBtn.setAttribute('aria-label', 'Pause Slideshow');
            }
            this.isAutoplaying = !this.isAutoplaying;
          });
          
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        // Manage focusability of slides
        slides.forEach((slide) => {
          const isActive = swiper.slides.indexOf(slide) === activeIndex;
          const focusableElements = FoxTheme.a11y.getFocusableElements(slide);

          focusableElements.forEach((el) => {
            if (isActive) {
              el.setAttribute('tabindex', '0');
            } else {
              el.setAttribute('tabindex', '-1');
            }
          });
        });

        this.updateControlsScheme(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls &&
              motionEls.forEach((motionEl) => {
                if (motionEl.hasAttribute('data-text')) {
                  motionEl.resetAnimation();
                }
              });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  motionEl && motionEl.refreshAnimation();
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      calcSlideHeight() {
        let maxHeight = 0;
        const slides = this.querySelectorAll('.swiper-slide');
        slides &&
          slides.forEach((slide) => {
            const slideText = slide.querySelector('.slideshow__text');
            if (slideText) {
              const slideTextHeight = slideText.offsetHeight;
              if (slideTextHeight > maxHeight) {
                maxHeight = slideTextHeight;
              }
            }
          });

        if (this.classList.contains('slideshow-controls-position--bottom')) {
          maxHeight += 100; // Avoid overlap with dots.
        }

        this.style.setProperty('--slide-height', maxHeight + 'px');
      }
    }
  );
}
